import * as React from 'react';
import type {View} from '@sail/engine';
import {createViewConfig} from '@sail/engine';
import {message, useIntl} from '@sail/intl';
import {flushSync} from 'react-dom';
import {view} from '../../view';

export type CodePuncherInputProps = View.IntrinsicElement<
  'input',
  {
    value: string;
    /**
     * The maximum length of the input.
     */
    maxLength: number;
    /**
     * Whether to disable autofill.
     */
    noAutofill?: boolean;
    /**
     * Whether to autofocus the input on mount.
     */
    autoFocus?: boolean;
    /**
     * Whether all digits have been entered.
     */
    isComplete?: boolean;
    /**
     * Callback when the input value changes.
     */
    onChange?: (value: string) => void;
  }
>;

export const CodePuncherInputConfig = createViewConfig({
  props: {} as CodePuncherInputProps,
  name: 'CodePuncherInput',
  defaults: {
    value: '',
    isComplete: false,
  },
});

const verificationCodeLabel = message({
  id: '@sail/ui.codePuncher.verificationCodeLabel',
  description: 'The aria-label for the verification code input',
  defaultMessage: 'Verification code',
});

const DEFAULT_PATTERN = '^\\d+$';

const updateSelection = (input: HTMLInputElement, position: number) => {
  input.setSelectionRange(position, position);
  // Safari doesn't fire selectionchange after programmatic caret updates.
  input.dispatchEvent(new Event('selectionchange'));
};

export const validateCode = (input: string, pattern: RegExp): string | null => {
  if (!input) {
    return '';
  }

  const sanitized = input.replace(/[-\s.]/g, '');

  if (sanitized.length > 0 && !pattern.test(sanitized)) {
    return null;
  }

  return sanitized;
};

export const CodePuncherInput = CodePuncherInputConfig.createView(
  ({value, maxLength, noAutofill, disabled, autoFocus, onChange, ...props}) => {
    const {formatMessage} = useIntl();
    const inputRef = React.useRef<HTMLInputElement>(null);
    const composingRef = React.useRef(false);
    const handledByKeyDownRef = React.useRef(false);

    React.useImperativeHandle(props.ref, () => inputRef.current!);

    // Handle autofocus on mount
    React.useLayoutEffect(() => {
      if (!autoFocus || disabled) {
        return;
      }

      const animationFrameId = requestAnimationFrame(() => {
        // eslint-disable-next-line @sail/ui-no-reflow
        inputRef.current?.focus();
      });

      return () => {
        cancelAnimationFrame(animationFrameId);
      };
    }, [autoFocus, disabled]);

    const handleOnPaste = React.useCallback<
      React.ClipboardEventHandler<HTMLInputElement>
    >(
      (event) => {
        if (!event.clipboardData) {
          return;
        }

        const content = event.clipboardData.getData('text/plain');

        event.preventDefault();

        const target = event.currentTarget;
        const value = target.value;
        const selectionStart = target.selectionStart || 0;
        const selectionEnd = selectionStart + content.length;
        const maxLength = target.maxLength || 0;

        const sanitizedContent = validateCode(
          content,
          new RegExp(target.pattern || DEFAULT_PATTERN),
        );

        if (sanitizedContent === null) {
          return;
        }

        const newValueUncapped =
          value.slice(0, selectionStart) +
          sanitizedContent +
          value.slice(selectionEnd);

        const newValue = newValueUncapped.slice(0, maxLength);

        inputRef.current?.dispatchEvent(new Event('selectionchange'));

        onChange?.(newValue);
      },
      [onChange],
    );

    const handleOnChange = React.useCallback<
      React.ChangeEventHandler<HTMLInputElement>
    >(
      (event) => {
        if (composingRef.current) {
          return;
        }

        if (handledByKeyDownRef.current) {
          handledByKeyDownRef.current = false;
          return;
        }

        /**
         * Early return if the value is not valid (contains non-digits)
         */
        const newValue = event.currentTarget.value.slice(
          0,
          event.currentTarget.maxLength,
        );

        const pattern = new RegExp(
          event.currentTarget.pattern || DEFAULT_PATTERN,
        );

        if (newValue.length > 0 && !pattern.test(newValue)) {
          return;
        }

        onChange?.(newValue);
      },
      [onChange],
    );

    const handleCompositionStart = React.useCallback(() => {
      composingRef.current = true;
    }, []);

    const handleCompositionEnd = React.useCallback<
      React.CompositionEventHandler<HTMLInputElement>
    >(
      (event) => {
        composingRef.current = false;

        handleOnChange(event as unknown as React.ChangeEvent<HTMLInputElement>);
      },
      [handleOnChange],
    );

    const handleOnKeyDown = React.useCallback<
      React.KeyboardEventHandler<HTMLInputElement>
    >(
      (event) => {
        const pattern = new RegExp(
          event.currentTarget.pattern || DEFAULT_PATTERN,
        );
        const isValidKey = pattern.test(event.key);
        const isActionKey = event.metaKey || event.ctrlKey;
        const isSpecialKey = [
          'Tab',
          'Enter',
          'Delete',
          'Backspace',
          'ArrowLeft',
          'ArrowRight',
        ].includes(event.key);

        // Allow all action keys
        if (isActionKey) {
          return;
        }

        // For other keys, ensure we're only allowing digits
        if (!isValidKey && !isSpecialKey) {
          event.preventDefault();
          return;
        }

        const input = event.currentTarget;
        const maxLength = input.maxLength;
        const selectionEnd = input.selectionEnd ?? 0;
        const selectionStart = input.selectionStart ?? 0;
        const inputValue = input.value;

        if (event.key === 'Backspace' && selectionStart >= 0) {
          if (selectionStart !== selectionEnd) {
            return;
          }

          event.preventDefault();

          const lastInputIndex = Math.max(inputValue.length - 1, 0);
          const selectionIndex = Math.min(
            selectionStart,
            Math.min(lastInputIndex, maxLength),
          );

          const shouldPreserveFocus =
            selectionIndex === lastInputIndex || selectionIndex === 0;
          const newValue =
            inputValue.slice(0, selectionIndex) +
            inputValue.slice(selectionIndex + 1);

          // Keep caret at the same position or move to the previous character
          const newSelectionIndex = shouldPreserveFocus
            ? selectionIndex
            : selectionIndex - 1;

          if (newValue !== inputValue) {
            flushSync(() => {
              onChange?.(newValue);
            });
          }

          updateSelection(input, newSelectionIndex);

          return;
        }

        // Allow default arrow key behavior
        if (event.key === 'ArrowLeft' || event.key === 'ArrowRight') {
          event.preventDefault();

          switch (event.key) {
            case 'ArrowLeft': {
              const newStart = Math.max(
                Math.min(selectionStart, maxLength - 1) - 1,
                0,
              );

              updateSelection(input, newStart);
              break;
            }
            case 'ArrowRight': {
              const newStart = Math.min(selectionStart + 1, maxLength - 1);

              updateSelection(input, newStart);
              break;
            }
            default: {
              break;
            }
          }

          return;
        }

        if (isValidKey) {
          event.preventDefault();

          const start = Math.min(selectionStart, maxLength - 1);

          const newValue =
            inputValue.slice(0, start) +
            event.key +
            inputValue.slice(start + 1);

          if (newValue !== inputValue) {
            handledByKeyDownRef.current = true;

            flushSync(() => {
              onChange?.(newValue.slice(0, maxLength));
            });
          }

          updateSelection(input, start + 1);
        }
      },
      [onChange],
    );

    return (
      <view.input
        {...props}
        ref={inputRef}
        type="tel"
        inputMode="numeric"
        autoComplete={noAutofill ? 'off' : 'one-time-code'}
        value={value}
        pattern={DEFAULT_PATTERN}
        disabled={disabled}
        maxLength={maxLength}
        data-lpignore="true"
        data-1p-ignore={noAutofill}
        aria-label={props['aria-label'] || formatMessage(verificationCodeLabel)}
        onPaste={handleOnPaste}
        onChange={handleOnChange}
        onKeyDown={handleOnKeyDown}
        onCompositionStart={handleCompositionStart}
        onCompositionEnd={handleCompositionEnd}
      />
    );
  },
  {
    css: {
      position: 'absolute',
      inset: 'space.0',
      padding: 'space.0',
      opacity: 1, // Mandatory for iOS hold-paste, maybe has to be "1"
      width: 'fill',
      height: 'fill',
      pointerEvents: 'all',
      userSelect: 'none',
      fontSize: '16px', // Prevent Safari auto-zoom on focus (requires >= 16px)
      color: 'transparent',
      caretColor: 'transparent',
      backgroundColor: 'transparent',
      border: '0 solid transparent',
      outline: '0 solid transparent',
      boxShadow: 'none',
      fontVariantNumeric: 'tabular-nums',
      '::selection': {
        color: 'transparent', // Keep text transparent
      },
    },
    variants: {
      isComplete: {
        true: {
          css: {
            width: 'space.1',
            height: 'space.1',
            left: '0px',
            top: '0px',
            inset: 'unset',
          },
        },
      },
    },
  },
);
